import { useState, useEffect } from "react";
import { useAtom } from "jotai";
import { GlobalVars } from "../../../state/global/globalVars";
import { eventStatusClassAtom } from "../../../state/schedule/eventStatusClasses";


import Section from "../../ui/content/Section";
import Button from "../../ui/form/Button";
import Calendar from "./Calendar";
import ReactModal from "react-modal";
import EventEdit from "./EventEdit";
import EventDetail from "./EventDetail";
import EventFinish from "./EventFinish";

import UilPlus from "@iconscout/react-unicons/icons/uil-plus";
import UilMessage from "@iconscout/react-unicons/icons/uil-message";
import UilSync from "@iconscout/react-unicons/icons/uil-sync";
import { type } from "@testing-library/user-event/dist/type";
import { installersAtom, installerTypeClassAtom, zonesAtom } from "../../../state/schedule/scheduleAtoms";

export default function Schedule({userId}) {
    const [eventStatus] = useAtom(eventStatusClassAtom);
    const [hideNS, setHideNS] = useState(false);
    const [eventModalOpen, setEventModalOpen] = useState(false);
    const [scheduleEventId, setScheduleEventId] = useState();
    const [eventEditModalOpen, setEventEditModalOpen] = useState(false);
    const [eventEditParams, setEventEditParams] = useState({});
    const [activeEventId, setActiveEventId] = useState();
    const [activeEvent, setActiveEvent] = useState();
    const [refreshCalendar, setRefreshCalendar] = useState(false);
    const [dropTechExport, setDropTechExport] = useState(false);
    const [selectedInstallerTypes, setSelectedInstallerTypes] = useState([]); // New state for selected installer types

    const [installers] = useAtom(installersAtom); // Use useAtom to get the value of installersAtom
    const [installerTypes] = useAtom(installerTypeClassAtom);
    useEffect(() => {
        if (installerTypes.length > 0) {
            setSelectedInstallerTypes(installerTypes.map(t => t.name));
        }
    }, [installerTypes]);


    const onNewJob = function(){ };

    const handleTypeSelection = (type) => {
        if (Array.isArray(type)) {
            // If "View All" is clicked
            if (selectedInstallerTypes.length === installerTypes.length) {
                // If all types are already selected, deselect all
                setSelectedInstallerTypes([]);
            } else {
                // Otherwise, select all types
                setSelectedInstallerTypes(installerTypes.map(t => t.name));
            }
        } else if (type === "Drop") {
            // Handle combined "Drop" and "Lead Drop" selection
            const combinedTypes = ["Drop", "Lead Drop"];
            const allSelected = combinedTypes.every(t => selectedInstallerTypes.includes(t));
            setSelectedInstallerTypes(prevTypes =>
                allSelected
                    ? prevTypes.filter(t => !combinedTypes.includes(t))
                    : [...new Set([...prevTypes, ...combinedTypes])]
            );
        }
        else {
            setSelectedInstallerTypes((prevTypes) =>
            prevTypes.includes(type)
                ? prevTypes.filter((t) => t !== type)
                : [...prevTypes, type]
            );
        }
    };

    const getInstallersByTypes = (types) => {
        return installers.filter(installer => types.includes(installer.InstallerType.name));
    };

    function newJob() {
        setEventEditParams(null);
        setActiveEvent(null);
        setEventEditModalOpen(true);
    }

    function exportDropTechSchedule() {
        setDropTechExport(true);
    }

    function triggerRefresh() {
        setRefreshCalendar(true);
    }

    function closeEventEditModal() {
        setEventEditModalOpen(false);
        setActiveEvent();
        setActiveEventId();
        triggerRefresh();
    }

    function openEditFromDetail(evnt) {
        setActiveEvent(evnt);
        setEventEditParams(null);
        closeEventModal();
        setEventEditModalOpen(true);
    }

    function closeEventModal() {
        setEventModalOpen(false);
        triggerRefresh();
    }
    const filteredInstallers = getInstallersByTypes(selectedInstallerTypes);


    return (
        <div>
            <div className="space-y-4">
                {/* Action buttons */ }
                <Section title="Actions">
                    <div className="flex flex-col sm:flex-row flex-wrap gap-2 lg:gap-y-4 lg:gap-x-4 items-stretch">
                        <Button
                            onClick = { newJob }
                            text = "New WO Task"
                            icon = { <UilPlus size={ GlobalVars.iconSize.xs } /> }
                            className = "md:w-40"
                        />
                        <Button
                            onClick = { exportDropTechSchedule }
                            text = "Daily Export"
                            icon = { <UilMessage size={ GlobalVars.iconSize.xs } /> }
                            className="md:w-40"
                        />
                        <Button
                            onClick = { triggerRefresh }
                            text = "Refresh"
                            icon = { <UilSync size={ GlobalVars.iconSize.xs } /> }
                            className = "md:w-40"
                        />
                    </div>
                </Section>
                {/* Checkboxes to filter Calendar Events */ }
                <Section title="Calendar Filter">
                    <p className="text-xs text-gray-500 mb-2">Click to toggle visibility</p>
                    <div className="flex flex-wrap gap-2">
                        <button
                            onClick={() => handleTypeSelection(["Drop","Install","ONT Lead","Senior Support","Repair","Field","Field Restoration","Consult Contractor"])}
                            className={`px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium border transition-colors ${
                                selectedInstallerTypes.length === installerTypes.length
                                    ? "bg-blue-600 text-white border-blue-600"
                                    : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"
                            }`}
                        >
                            Toggle All
                        </button>

                        {[
                            { name: "Field", handler: "Field" },
                            { name: "Field Restoration", handler: "Field Restoration" },
                            { name: "Consult Contractor", handler: "Consult Contractor" },
                            { name: "Drop", handler: "Drop" },
                            { name: "Drop Contractor", handler: "Drop Contractor" },
                            { name: "ONT Lead", handler: "ONT Lead" },
                            { name: "Install", handler: "Install" },
                            { name: "Install Contractor", handler: "Install Contractor" },
                            { name: "Senior Support", handler: "Senior Support" },
                            { name: "Repair", handler: "Repair" },
                        ].map(({ name, handler }) => {
                            const isActive = name === "Drop"
                                ? selectedInstallerTypes.includes("Drop") && selectedInstallerTypes.includes("Lead Drop")
                                : selectedInstallerTypes.includes(name);
                            const typeClass = installerTypes.find(t => t.name === name)?.class || "";
                            return (
                                <button
                                    key={name}
                                    onClick={() => handleTypeSelection(handler)}
                                    className={`px-3 py-1.5 rounded-full text-xs sm:text-sm font-medium border transition-colors ${
                                        isActive
                                            ? `${typeClass} text-white border-transparent`
                                            : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"
                                    }`}
                                >
                                    {name}
                                </button>
                            );
                        })}
                    </div>
                </Section>



                <Section title="Timeline" className="space-y-1 lg:space-y-4">
                    {/* Labels for job types */}
                    <div className="grid grid-row grid-flow-col grid-rows-3 sm:grid-fow-row sm:grid-rows-2 lg:grid-rows-1 grid-cols-3 sm:grid-cols-4 lg:grid-cols-8 gap-1 lg:gap-2">
                        {
                            selectedInstallerTypes.map((status) => (
                                    <div key={ status.status_name } className={ `relative w-4 h-4 sm:w-full p-0 lg:py-2 sm:h-auto rounded text-xs sm:text-sm lg:text-base text-light-0 sm:text-light-11 text-center ${status.class}`}>
                                        <div className="absolute sm:relative left-5 sm:left-0 whitespace-nowrap">
                                            { status.status_name }
                                        </div>
                                    </div>
                                )
                            )
                        }
                    </div>

                    {/* Calendar */}
                    <Calendar
                        userId = { userId }
                        onNewJob = { onNewJob }
                        eventStatus = { eventStatus }
                        hideNS = { hideNS }
                        setHideNS = { setHideNS }
                        setEventModalOpen = { setEventModalOpen }
                        setEventEditModalOpen = { setEventEditModalOpen }
                        setEventEditParams = { setEventEditParams }
                        setActiveEvent = { setActiveEvent }
                        setActiveEventId = { setActiveEventId }
                        refreshCalendar = { refreshCalendar }
                        setRefreshCalendar = { setRefreshCalendar }
                        dropTechExport = { dropTechExport }
                        setDropTechExport = { setDropTechExport }
                        selectedInstallerTypes={selectedInstallerTypes}
                        ariaHideApp = { false }
                    />
                </Section>

                {/* Modals */}
                <ReactModal
                    isOpen = { eventModalOpen }
                    onRequestClose = { closeEventModal }
                    contenLabel = "Work Order Task Detail"
                    className = "modal max-w-[900px]"
                    overlayClassName = "modal_overlay"
                    closeTimeoutMS = { 200 }
                    ariaHideApp = { false }
                    style = { GlobalVars.styles.modal } >
                    <EventDetail
                        eventId = { activeEventId }
                        eventEditParams = { eventEditParams }
                        closeEventModal = { closeEventModal }
                        openEventEditModal = { openEditFromDetail }
                />
                </ReactModal>

                <ReactModal
                    isOpen = { eventEditModalOpen }
                    onRequestClose = { closeEventEditModal }
                    contentLabel = "New Work Order Task"
                    className = "modal max-w-[900px]"
                    overlayClassName = "modal_overlay"
                    closeTimeoutMS = { 200 }
                    ariaHideApp = { false }
                    style = { GlobalVars.styles.modal } >
                    <EventEdit eventEditParams={ eventEditParams } closeEventModal={ closeEventEditModal } activeEvent={ activeEvent } />
                </ReactModal>
            </div>
        </div>
    );
}